import React from "react";
import { CaseLinkCell, OpsCaseLinkCell } from "../components/grid/cells/CaseLinkCell";
import { DateFilter } from "../components/inputs/DateFilter";
import { formatDateTime } from "../util/formatters";
import { FaxActionsCell } from "./FaxActionsCell";
import { EnumFilter } from "../components/inputs/EnumFilter";
import { isBlank } from "../util/helpers";

// This is not really a hook, it just follows a naming
// convention for hooks, and so it triggers eslint.
/* eslint-disable react-hooks/rules-of-hooks */
export const useColumns = ({ isInternalUser, sent = false, discarded = false, labOptions = [], clinicOptions = [], labId, clinicId }) => {
  if (sent) {
    return useSentColumns(isInternalUser);
  } else if (discarded) {
    return useTrashColumns(isInternalUser, labOptions, clinicOptions);
  } else {
    return useInboxColumns(isInternalUser, labId, clinicId);
  }
};
/* eslint-enable react-hooks/rules-of-hooks */

const useInboxColumns = (isInternalUser, labId, clinicId) => {
  const columns = [
    {
      Header: "Fax Id",
      accessor: "id",
    },
    {
      Header: "Created Date",
      id: "created_at",
      accessor: (r) => formatDateTime(r.created_at),
      Filter: (props) => <DateFilter {...props} />,
    },
    {
      Header: "Sender Fax Number",
      accessor: "fax_number",
    },
    {
      Header: "Sender Name",
      accessor: "company_name",
    },
    {
      Header: "Status",
      accessor: "status",
    },
    {
      Header: "Associated Case",
      accessor: "case_id",
      Cell: isInternalUser ? OpsCaseLinkCell : CaseLinkCell,
    },
    {
      Header: "Lab",
      accessor: "lab.lab_name",
      disableFilters: true,
    },
    {
      Header: "Clinic",
      accessor: "clinic.clinic_name",
      disableFilters: true,
    },
    {
      Header: "Actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: FaxActionsCell,
      width: 200,
    },
  ];

  return columns.filter((column) => {
    // if clinicId is present, that means we're on the Clinic Fax view, so remove the lab column
    if (!isBlank(clinicId) && column.Header === "Lab") return false;

    // if labId is present, that means we're on the Lab Fax view, so remove the clinic column
    if (!isBlank(labId) && column.Header === "Clinic") return false;

    // If both arguments are blank, we're on the inbox dashboard, so keep both columns
    return true;
  });
};

const useSentColumns = (isInternalUser) => [
  {
    Header: "Fax Id",
    accessor: "id",
  },
  {
    Header: "Sent Date",
    id: "created_at",
    accessor: (r) => formatDateTime(r.created_at),
    Filter: (props) => <DateFilter {...props} />,
  },
  {
    Header: "Recipient Fax Number",
    accessor: "fax_number",
  },
  {
    Header: "Status",
    accessor: "status",
  },
  {
    Header: "Associated Case",
    accessor: "case_id",
    Cell: isInternalUser ? OpsCaseLinkCell : CaseLinkCell,
  },
  {
    Header: "Actions",
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ row }) => <FaxActionsCell row={row} attachmentAllowed={false} />,
    width: 200,
  },
];

const useTrashColumns = (isInternalUser, labOptions, clinicOptions) => [
  {
    Header: "Fax Id",
    accessor: "id",
  },
  {
    Header: "Created Date",
    id: "created_at",
    accessor: (r) => formatDateTime(r.created_at),
    Filter: (props) => <DateFilter {...props} />,
  },
  {
    Header: "Mailbox",
    id: "fax_type",
    accessor: (r) => (r.fax_type == "inbound" ? "Inbox" : "Sent"),
    Filter: (props) => (
      <EnumFilter
        {...props}
        options={[
          { value: "inbound", label: "Inbox" },
          { value: ["outbound", "mail", null], label: "Sent" },
        ]}
      />
    ),
  },
  {
    Header: "Fax Number",
    accessor: "fax_number",
  },
  {
    Header: "Name",
    accessor: "recipient_name",
  },
  {
    Header: "Status",
    accessor: "status",
  },
  {
    Header: "Associated Case",
    accessor: "case_id",
    Cell: isInternalUser ? OpsCaseLinkCell : CaseLinkCell,
  },
  {
    Header: "Lab",
    accessor: "lab.lab_name",
    id: "lab_id",
    Filter: (props) => <EnumFilter {...props} options={labOptions} />,
  },
  {
    Header: "Clinic",
    accessor: "clinic.clinic_name",
    id: "clinic_id",
    Filter: (props) => <EnumFilter {...props} options={clinicOptions} />,
  },
  {
    Header: "Actions",
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ row }) => <FaxActionsCell row={row} attachmentAllowed={false} />,
    width: 200,
  },
];
